import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import { getProductsPurchasePrice } from 'interfaces/products';
import { createPricing, getPricing, updateSpecificPricing } from 'interfaces/pricing';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MdSave } from 'react-icons/md';
import { getTotalHT, toNumber, DECIMAL_INPUT_REGEX } from '../utils';

interface LotRow {
    lotNumber: string;
    quantity: number;
}

// `item.preparationDetails` (when present) is the source of truth for which
// physical lots were picked for this line — it's what lets stock removal
// decrement the real lot(s) instead of guessing via FIFO. `item.lotNumber`
// stays as a derived single-value mirror for anything still reading it
// (delivery note, invoice, exports).
const rowsFromItem = (item: any): LotRow[] => {
    if (item.preparationDetails && item.preparationDetails.length > 0) {
        return item.preparationDetails.map((p: any) => ({
            lotNumber: p.lotNumber || '',
            quantity: Number(p.quantityPicked) || 0,
        }));
    }
    return [{ lotNumber: item.lotNumber || '', quantity: Number(item.quantity) || 0 }];
};

const LotInput = ({
    item,
    index,
    mobile = false,
    products,
    isDisabled,
    handleLotNumberChange,
}: {
    item: any;
    index: number;
    mobile?: boolean;
    products: any;
    isDisabled: boolean;
    handleLotNumberChange: (item: any, value: string | any[]) => void;
}) => {
    const fullProduct = products?.find((p: any) => p._id === item.productId);
    const inventoryLines = (fullProduct?.inventory ?? []).filter(
        (inv: any) => inv.lotNumber && inv.quantity > 0
    );
    const uniqueLots = Array.from(
        new Set(inventoryLines.map((inv: any) => inv.lotNumber as string))
    ) as string[];
    const datalistId = `lot-${mobile ? 'm-' : ''}${item.productId}-${index}`;
    const [rows, setRows] = useState<LotRow[]>(() => rowsFromItem(item));
    const isPicking = useRef(false);

    useEffect(() => {
        setRows(rowsFromItem(item));
    }, [item.lotNumber, item.preparationDetails]);

    // A single lot keeps writing the plain `lotNumber` string (today's
    // behaviour, untouched). Only once a line is split across several lots
    // do we switch to the richer `preparationDetails` allocation.
    const commit = (nextRows: LotRow[]) => {
        const cleaned = nextRows.filter((r) => r.lotNumber.trim() !== '');
        if (cleaned.length <= 1) {
            handleLotNumberChange(item, cleaned[0]?.lotNumber || '');
            return;
        }
        const preparationDetails = cleaned.map((r) => {
            const match = inventoryLines.find((inv: any) => inv.lotNumber === r.lotNumber);
            return {
                lotNumber: r.lotNumber,
                quantityPicked: Number(r.quantity) || 0,
                inventoryId: match?._id,
                area: match?.area,
                dlc: match?.dlc,
            };
        });
        handleLotNumberChange(item, preparationDetails);
    };

    const updateRow = (i: number, patch: Partial<LotRow>) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
        setRows(next);
        return next;
    };

    const removeRow = (i: number) => {
        const next = rows.filter((_, idx) => idx !== i);
        const finalRows = next.length ? next : [{ lotNumber: '', quantity: Number(item.quantity) || 0 }];
        setRows(finalRows);
        commit(finalRows);
    };

    const isMulti = rows.length > 1;
    const total = rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
    const mismatch = isMulti && total !== Number(item.quantity);

    return (
        <div className={isMulti ? 'flex flex-col gap-1 border rounded-lg p-1.5 bg-gray-50' : 'flex items-center gap-1'}>
            {rows.map((row, i) => (
                <div key={i} className="flex items-center gap-1">
                    <input
                        type="text"
                        list={uniqueLots.length > 0 ? datalistId : undefined}
                        className={`h-[26px] ${isMulti ? 'w-[76px]' : 'w-[90px]'} border rounded-lg px-2 text-xs text-gray-600 focus:outline-none focus:ring focus:border-blue-300`}
                        value={row.lotNumber}
                        placeholder="N° lot"
                        disabled={isDisabled}
                        onChange={(e) => {
                            const val = e.target.value;
                            const next = updateRow(i, { lotNumber: val });
                            if (uniqueLots.includes(val)) {
                                isPicking.current = true;
                                commit(next);
                            }
                        }}
                        onBlur={() => {
                            if (!isPicking.current) commit(rows);
                            isPicking.current = false;
                        }}
                    />
                    {isMulti && (
                        <>
                            <input
                                type="number"
                                min={0}
                                className="h-[26px] w-[48px] border rounded-lg px-1 text-xs text-gray-600 focus:outline-none focus:ring focus:border-blue-300"
                                value={row.quantity}
                                disabled={isDisabled}
                                onChange={(e) => updateRow(i, { quantity: toNumber(e.target.value) })}
                                onBlur={() => commit(rows)}
                            />
                            {!isDisabled && (
                                <button
                                    type="button"
                                    className="text-red-400 text-xs leading-none px-0.5"
                                    onClick={() => removeRow(i)}
                                >
                                    ×
                                </button>
                            )}
                        </>
                    )}
                </div>
            ))}
            {uniqueLots.length > 0 && (
                <datalist id={datalistId}>
                    {uniqueLots.map((lot, i) => <option key={i} value={lot} />)}
                </datalist>
            )}
            {!isDisabled && (
                <div className="flex items-center justify-between gap-1">
                    <button
                        type="button"
                        className="text-[10px] text-toola-400 hover:text-toola-500"
                        onClick={() => setRows([...rows, { lotNumber: '', quantity: 0 }])}
                    >
                        + lot
                    </button>
                    {isMulti && (
                        <span className={`text-[10px] ${mismatch ? 'text-red-500' : 'text-gray-400'}`}>
                            {total}/{item.quantity}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const OrderTable = (props: {
    order: any;
    handleQtyChange: any;
    handlePriceChange: any;
    handleDiscountTypeChange: any;
    handleDiscountChange: any;
    handleLotNumberChange: any;
    handleCommentChange: any;
    handlePackagingChange: any;
    onDelete: any;
    products: any;
    customerId?: string;
}) => {
    const {
        order,
        handleQtyChange,
        handlePriceChange,
        onDelete,
        handleDiscountChange,
        handleDiscountTypeChange,
        handleCommentChange,
        handleLotNumberChange,
        handlePackagingChange,
        products,
        customerId,
    } = props;

    const [isCommentSectionOpen, setIsCommentSectionOpen] = useState<any>({});
    const [savedCustomPrices, setSavedCustomPrices] = useState<Set<string>>(new Set());
    const [existingPricings, setExistingPricings] = useState<Map<string, any>>(new Map());
    // Raw text buffer for decimal inputs, keyed by "<item>:<field>", so a
    // trailing "," or "." typed by the user isn't reformatted away before
    // they can type the next digit.
    const [rawInputs, setRawInputs] = useState<{ [key: string]: string }>({});
    // Bumped once purchase prices come back from initProductsPrice(), which
    // mutates item.totalPurchasePrice in place (no setState) — this just
    // forces the margin % column to re-render without introducing a second,
    // lagging copy of `order` that the price/qty inputs would render from.
    const [, forcePurchasePriceRefresh] = useState(0);
    // Kept current every render so the async purchase-price fetch below can
    // mutate whatever items are *currently* live, even if the user edited an
    // item (swapping it for a new object reference) while the fetch was in flight.
    const orderRef = useRef(order);
    orderRef.current = order;

    const isDisabled = false;

    const itemKey = (item: any) => String(item.productId ?? item._id ?? item.name);

    const getDisplayValue = (key: string, committed: any) => {
        const raw = rawInputs[key];
        return raw !== undefined ? raw : (committed ?? '');
    };

    const handleDecimalInputChange = (
        key: string,
        rawValue: string,
        commit: (value: string) => void,
    ) => {
        if (!DECIMAL_INPUT_REGEX.test(rawValue)) return;
        setRawInputs((prev) => ({ ...prev, [key]: rawValue }));
        commit(rawValue);
    };

    // Dropped once the field loses focus so the display falls back to the
    // committed/normalized value instead of keeping the exact typed text forever.
    const clearRawInput = (key: string) => {
        setRawInputs((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    useEffect(() => {
        if (!customerId) return;
        getPricing(customerId, 1, 1000).then((pricingList) => {
            if (!pricingList?.data) return;
            const map = new Map<string, any>();
            for (const p of pricingList.data) {
                const productId = String(p.productId?._id ?? p.productId);
                map.set(productId, p);
            }
            setExistingPricings(map);
        });
    }, [customerId]);

    const handleSaveCustomPrice = async (item: any) => {
        if (!customerId) return;
        const productId = String(item.productId);
        const existing = existingPricings.get(productId);
        if (existing) {
            const res = await updateSpecificPricing(existing._id, {
                productId: item.productId,
                price: parseFloat(item.price),
                tags: existing.tags,
            });
            if (res) {
                setExistingPricings(prev => {
                    const next = new Map(prev);
                    next.set(productId, { ...existing, price: parseFloat(item.price) });
                    return next;
                });
                setSavedCustomPrices(prev => new Set([...prev, item.productId]));
                toast.success(`Prix personnalisé mis à jour pour ${item.name}`);
            }
        } else {
            const res = await createPricing({
                productId: item.productId,
                customerId,
                price: parseFloat(item.price),
            });
            if (res) {
                setExistingPricings(prev => {
                    const next = new Map(prev);
                    next.set(productId, { _id: res._id ?? res.data?._id, productId: item.productId, price: parseFloat(item.price) });
                    return next;
                });
                setSavedCustomPrices(prev => new Set([...prev, item.productId]));
                toast.success(`Prix personnalisé enregistré pour ${item.name}`);
            }
        }
    };

    const initProductsPrice = async () => {
        const productIds = order.items.map((item: any) => item.productId);
        if (productIds.length === 0) return;
        const res = await getProductsPurchasePrice(productIds);
        if (!res) return;
        // Mutates the live item objects in place (same references held by the
        // parent's order state) so totalPurchasePrice reaches the save
        // payload without needing a setNewOrder round-trip. Reads items from
        // orderRef (not the `order` closed over above) so an item edited
        // while this fetch was in flight still gets enriched on its current
        // object, not an orphaned pre-edit one.
        orderRef.current.items.forEach((item: any) => {
            const product = res.data.find((p: any) => p.id === item.productId);
            if (product) item.totalPurchasePrice = product?.marginData?.purchasePrice;
        });
        forcePurchasePriceRefresh((n) => n + 1);
    };

    // Only product additions/removals should trigger a refetch — not every
    // keystroke on price/qty/discount, which would otherwise refire this on
    // every edit (order is a new object reference each time).
    const productIdsKey = order.items.map((item: any) => item.productId).join(',');

    useEffect(() => {
        initProductsPrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIdsKey]);

    const getMarginPct = (item: any, sellPrice: number) => {
        const totalCost = item.totalPurchasePrice * item.quantity;
        if (!totalCost || !sellPrice) return null;
        return `${(((sellPrice - totalCost) / sellPrice) * 100).toFixed(1)} %`;
    };

    /* ── Sub-components ── */

    const PackagingSelect = ({ item }: { item: any }) => {
        const fullProduct = products?.find((p: any) => p._id === item.productId);
        if (!fullProduct?.packagings?.length)
            return <span className="text-xs text-gray-600">{item.unity || '—'}</span>;
        const currentIndex = item.packagingRef
            ? fullProduct.packagings.findIndex((p: any) => p.ref === item.packagingRef)
            : item.packagingUnity
            ? fullProduct.packagings.findIndex((p: any) => p.unity === item.packagingUnity)
            : -1;
        return (
            <select
                className="h-[26px] px-2 text-xs border border-gray-200 rounded-lg bg-white max-w-[140px]"
                value={currentIndex >= 0 ? currentIndex.toString() : ''}
                onChange={(e) => {
                    if (e.target.value === '') handlePackagingChange(item, null);
                    else {
                        const idx = parseInt(e.target.value);
                        handlePackagingChange(item, fullProduct.packagings[idx]);
                    }
                }}
            >
                <option value="">{fullProduct.unity} – {fullProduct.price} €</option>
                {fullProduct.packagings.map((pkg: any, idx: number) => (
                    <option key={idx} value={idx.toString()}>
                        {pkg.unity} ({pkg.weight} {fullProduct.unity}) – {pkg.price} €
                    </option>
                ))}
            </select>
        );
    };

    const SavePriceHint = ({ item }: { item: any }) => {
        if (!customerId) return null;
        const alreadySaved = savedCustomPrices.has(item.productId);
        if (alreadySaved) return <span className="text-[10px] text-green-500">✓ Enregistré</span>;

        const itemPrice = parseFloat(item.price);
        const existing = existingPricings.get(String(item.productId));

        if (existing) {
            if (Math.abs(parseFloat(existing.price) - itemPrice) < 0.001) return null;
            return (
                <button
                    className="flex items-center gap-0.5 text-[10px] text-toola-500 hover:text-toola-600"
                    onClick={() => handleSaveCustomPrice(item)}
                >
                    <MdSave size={10} />
                    Màj prix perso.
                </button>
            );
        }

        const fullProduct = products?.find((p: any) => p._id === item.productId);
        const priceChanged = fullProduct && Math.abs(parseFloat(fullProduct.price) - itemPrice) > 0.001;
        if (!priceChanged) return null;
        return (
            <button
                className="flex items-center gap-0.5 text-[10px] text-toola-500 hover:text-toola-600"
                onClick={() => handleSaveCustomPrice(item)}
            >
                <MdSave size={10} />
                Enregistrer prix perso.
            </button>
        );
    };

    return (
        <div className="w-full mt-2">

            {/* ── Desktop table ── */}
            <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                            {['Produit', 'Conditionnement', 'Quantité', 'Prix unit. HT', 'Remise', 'Total HT'].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                                    {h}
                                </th>
                            ))}
                            <th className="px-3 py-2 w-[60px]" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {order.items.map((item: any, index: number) => (
                            <tr key={`${item.name}-${index}`} className="hover:bg-gray-50/60 transition-colors">

                                {/* Produit */}
                                <td className="px-3 py-2 max-w-[220px]">
                                    <p className="text-sm font-medium text-navy-700 dark:text-white truncate leading-tight">{item.name}</p>
                                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        <span className="text-[11px] text-gray-600">{item.ref}</span>
                                        <LotInput item={item} index={index} products={products} isDisabled={isDisabled} handleLotNumberChange={handleLotNumberChange} />
                                    </div>
                                    {(isCommentSectionOpen[`${item.name}-${item.ref}`] || item.comment) ? (
                                        <div className="mt-1">
                                            <textarea
                                                className="w-full h-8 px-2 py-1 text-xs border rounded-lg focus:outline-none focus:ring focus:border-blue-300 resize-none"
                                                placeholder="Commentaire..."
                                                value={item.comment || ''}
                                                onChange={(e) => handleCommentChange(item, e.target.value)}
                                            />
                                            <button
                                                className="text-[10px] text-red-400 hover:text-red-500"
                                                onClick={() => {
                                                    setIsCommentSectionOpen({ ...isCommentSectionOpen, [`${item.name}-${item.ref}`]: false });
                                                    handleCommentChange(item, '');
                                                }}
                                            >
                                                Retirer
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="text-[10px] text-toola-400 hover:text-toola-500"
                                            onClick={() => setIsCommentSectionOpen({ ...isCommentSectionOpen, [`${item.name}-${item.ref}`]: true })}
                                        >
                                            + commentaire
                                        </button>
                                    )}
                                </td>

                                {/* Conditionnement */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <PackagingSelect item={item} />
                                </td>

                                {/* Quantité */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    {(() => {
                                        const fullProduct = products?.find((p: any) => p._id === item.productId);
                                        const isBillByBase = item.packagingBillByBaseUnit === true && item.packagingWeight;
                                        const totalBase = isBillByBase
                                            ? parseFloat(item.quantity) * parseFloat(item.packagingWeight)
                                            : null;
                                        return (
                                            <div>
                                                <div className="flex items-center gap-1">
                                                    <InputField
                                                        extra="w-[60px]"
                                                        placeholder="Qté"
                                                        id={`qty-input-${index}`}
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={getDisplayValue(`${itemKey(item)}:qty`, item.quantity)}
                                                        onChange={(e: any) =>
                                                            handleDecimalInputChange(
                                                                `${itemKey(item)}:qty`,
                                                                e.target.value,
                                                                (v) => handleQtyChange(item, v),
                                                            )
                                                        }
                                                        onClick={(e: any) => e.target.select()}
                                                        onBlur={() => clearRawInput(`${itemKey(item)}:qty`)}
                                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                            if (e.key === 'ArrowDown') {
                                                                e.preventDefault();
                                                                const next = document.getElementById(`qty-input-${index + 1}`) as HTMLInputElement | null;
                                                                if (next) { next.focus(); setTimeout(() => next.select(), 0); }
                                                            } else if (e.key === 'ArrowUp') {
                                                                e.preventDefault();
                                                                const prev = document.getElementById(`qty-input-${index - 1}`) as HTMLInputElement | null;
                                                                if (prev) { prev.focus(); setTimeout(() => prev.select(), 0); }
                                                            }
                                                        }}
                                                        inputSmall={true}
                                                    />
                                                    <span className="text-sm font-medium text-gray-700 truncate max-w-[65px]">
                                                        {item.packagingUnity || item.unity}
                                                    </span>
                                                </div>
                                                {isBillByBase && totalBase !== null && (
                                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                                        = {totalBase % 1 === 0 ? totalBase : totalBase.toFixed(2)} {fullProduct?.unity}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </td>

                                {/* Prix unitaire */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="flex items-center gap-1">
                                        <InputField
                                            extra="w-[80px]"
                                            placeholder="Prix"
                                            id={item.name + '_up'}
                                            type="text"
                                            inputMode="decimal"
                                            value={getDisplayValue(`${itemKey(item)}:price`, item.price)}
                                            onChange={(e: any) =>
                                                handleDecimalInputChange(
                                                    `${itemKey(item)}:price`,
                                                    e.target.value,
                                                    (v) => handlePriceChange(item, v),
                                                )
                                            }
                                            onBlur={() => clearRawInput(`${itemKey(item)}:price`)}
                                            inputSmall={true}
                                        />
                                        <span className="text-xs text-gray-600">€</span>
                                    </div>
                                    <SavePriceHint item={item} />
                                </td>

                                {/* Remise */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="flex items-center gap-1">
                                        <div className="w-[72px]">
                                            <SearchSelector
                                                displayKey="name"
                                                value={item.discountType || 'none'}
                                                options={[
                                                    { name: 'Aucune', _id: 'none' },
                                                    { name: '%', _id: 'percent' },
                                                    { name: '€', _id: 'value' },
                                                ]}
                                                onSelect={(e: any) => handleDiscountTypeChange(item, e._id)}
                                                inputSmall={true}
                                            />
                                        </div>
                                        {item.discountType && item.discountType !== 'none' && (
                                            <div className="flex items-center gap-0.5">
                                                <InputField
                                                    extra="w-[50px]"
                                                    placeholder="0"
                                                    id={item.name + '_discount'}
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={getDisplayValue(`${itemKey(item)}:discount`, item.discountValue)}
                                                    onChange={(e: any) =>
                                                        handleDecimalInputChange(
                                                            `${itemKey(item)}:discount`,
                                                            e.target.value,
                                                            (v) => handleDiscountChange(item, v),
                                                        )
                                                    }
                                                    onBlur={() => clearRawInput(`${itemKey(item)}:discount`)}
                                                    inputSmall={true}
                                                />
                                                <span className="text-xs text-gray-600">
                                                    {item.discountType === 'percent' ? '%' : '€'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </td>

                                {/* Total HT */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                        {getTotalHT([item]).toFixed(2)} €
                                    </p>
                                    {getMarginPct(item, getTotalHT([item])) && (
                                        <p className="text-xs font-medium text-gray-700">
                                            Marge {getMarginPct(item, getTotalHT([item]))}
                                        </p>
                                    )}
                                </td>

                                {/* Action */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <button
                                        className="px-2 py-1 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                                        onClick={() => onDelete(item)}
                                    >
                                        Retirer
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="sm:hidden flex flex-col divide-y divide-gray-100">
                {order.items.map((item: any, index: number) => (
                    <div key={`${item.name}-${index}-m`} className="py-3">
                        {/* Nom + suppr */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-navy-700 truncate">{item.name}</p>
                                <p className="text-[11px] text-gray-600">{item.ref}</p>
                            </div>
                            <button
                                className="flex-shrink-0 px-2 py-1 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                                onClick={() => onDelete(item)}
                            >
                                Retirer
                            </button>
                        </div>

                        {/* Qté + prix */}
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-600">Qté :</span>
                                    <InputField
                                        extra="w-[55px]"
                                        inputSmall={true}
                                        placeholder="Qté"
                                        id={item.name + '_qty_m'}
                                        type="text"
                                        inputMode="decimal"
                                        value={getDisplayValue(`${itemKey(item)}:qty`, item.quantity)}
                                        onChange={(e: any) =>
                                            handleDecimalInputChange(
                                                `${itemKey(item)}:qty`,
                                                e.target.value,
                                                (v) => handleQtyChange(item, v),
                                            )
                                        }
                                        onClick={(e: any) => e.target.select()}
                                        onBlur={() => clearRawInput(`${itemKey(item)}:qty`)}
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        {item.packagingUnity || item.unity}
                                    </span>
                                </div>
                                {item.packagingBillByBaseUnit === true && item.packagingWeight && (() => {
                                    const fullProduct = products?.find((p: any) => p._id === item.productId);
                                    const totalBase = parseFloat(item.quantity) * parseFloat(item.packagingWeight);
                                    return (
                                        <p className="text-[10px] text-gray-500">
                                            = {totalBase % 1 === 0 ? totalBase : totalBase.toFixed(2)} {fullProduct?.unity}
                                        </p>
                                    );
                                })()}
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-600">PU :</span>
                                <InputField
                                    extra="w-[65px]"
                                    inputSmall={true}
                                    placeholder="Prix"
                                    id={item.name + '_up_m'}
                                    type="text"
                                    inputMode="decimal"
                                    value={getDisplayValue(`${itemKey(item)}:price`, item.price)}
                                    onChange={(e: any) =>
                                        handleDecimalInputChange(
                                            `${itemKey(item)}:price`,
                                            e.target.value,
                                            (v) => handlePriceChange(item, v),
                                        )
                                    }
                                    onBlur={() => clearRawInput(`${itemKey(item)}:price`)}
                                />
                                <span className="text-xs text-gray-600">€</span>
                            </div>
                            <p className="text-sm font-semibold text-navy-700">
                                = {getTotalHT([item]).toFixed(2)} €
                            </p>
                        </div>

                        {/* Lot + conditionnement */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <LotInput item={item} index={index} mobile products={products} isDisabled={isDisabled} handleLotNumberChange={handleLotNumberChange} />
                            <PackagingSelect item={item} />
                        </div>

                        <div className="mt-1">
                            <SavePriceHint item={item} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OrderTable;
